import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Activity, AlertCircle, ArrowLeft, FileArchive, FileText, Handshake, IdCard, MessagesSquare, Package, RefreshCw, User, Users } from 'lucide-react';
import { partnersApi, PartnersApiError } from '../services/partnersApi';
import { useAuth } from '../contexts/AuthContext';
import { useDetailSection } from '../hooks/useDetailSection';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useNowSeconds } from '../hooks/useNowSeconds';
import { useVisiblePolling } from '../hooks/useVisiblePolling';
import { useTourNavigation } from '../hooks/useTourNavigation';
import { DetailSections } from '../components/ui/DetailSections';
import { EmptyState } from '../components/ui/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';
import { PanelTour } from '../components/ui/PanelTour';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { CreatorDetailModal } from '../components/partnerships/CreatorDetailModal';
import { PartnershipStatusBadge } from '../components/partnerships/PartnershipStatusBadge';
import { PartnerActivityTimeline } from '../components/partners/PartnerActivityTimeline';
import { PartnerDiscordSection } from '../components/partners/PartnerDiscordSection';
import { PartnerNoticeBar } from '../components/partners/PartnerNoticeBar';
import { PartnerPacksSection } from '../components/partners/PartnerPacksSection';
import { PartnerServerPacksSection } from '../components/partners/PartnerServerPacksSection';
import { PartnerChannelBadge, PartnerPhaseBadge } from '../components/partners/PartnerPhaseBadge';
import { PartnerTeamSection } from '../components/partners/PartnerTeamSection';
import { PartnerUpdatesSection } from '../components/partners/PartnerUpdatesSection';
import { detailSections } from '../config/detailSections';
import { partnerPageTour } from '../config/panelTours';
import type { PartnershipStatus } from '../types/partnership';
import type { PartnerDetail } from '../types/partners';
import { cn } from '../utils/cn';
import { getPartnershipPerms } from '../utils/partnershipPermissions';
import { canViewModpackConfigs } from '../utils/modpackConfigPermissions';
import { memberName } from '../utils/partnerLinks';
import type { PartnerNotice } from '../utils/partnerSync';

type SectionId = 'team' | 'discord' | 'updates' | 'packs' | 'server-packs' | 'activity';

const TABS: { id: SectionId; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { id: 'team', label: 'Team', icon: Users },
  { id: 'discord', label: 'Discord', icon: MessagesSquare },
  { id: 'updates', label: 'Update reports', icon: FileText },
  { id: 'packs', label: 'Packs', icon: Package },
  { id: 'server-packs', label: 'Server packs', icon: FileArchive },
  { id: 'activity', label: 'Activity', icon: Activity },
];

/**
 * One partner team: who is on it, their packs and what happened. Argus is the
 * one place the team is kept, so this page is where staff add and remove
 * people; Mr. Roboto keeps Discord matching it.
 */
export function PartnerView() {
  const { creatorId = '' } = useParams<{ creatorId: string }>();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getPartnershipPerms(identity, isDev), [identity, isDev]);
  const { tourNavigation } = useTourNavigation();
  const canOpenConfigs = isDev || canViewModpackConfigs(identity);

  const [detail, setDetail] = useState<PartnerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  const [notice, setNotice] = useState<PartnerNotice | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreator, setShowCreator] = useState(false);
  const [section, setSection] = useDetailSection(TABS, 'team');
  const now = useNowSeconds(15_000) * 1000;
  // What Mr. Roboto last reported, so a background poll only refreshes the timeline when that moved.
  const lastObserved = useRef<number | null>(null);

  useDocumentTitle(detail ? `Partner: ${detail.creator.name}` : 'Partner');

  const load = useCallback(
    async (quiet = false, poll = false) => {
      if (!creatorId) return;
      if (quiet && !poll) setRefreshing(true);
      else if (!quiet) setLoading(true);
      try {
        const next = await partnersApi.get(creatorId);
        setDetail(next);
        setError(null);
        if (!poll || next.discord.observed_at !== lastObserved.current) setRefreshKey((k) => k + 1);
        lastObserved.current = next.discord.observed_at;
      } catch (e) {
        // A quiet refresh keeps showing what was there; only a first load fails the page.
        if (!quiet) setError({ status: e instanceof PartnersApiError ? e.status : 0, message: e instanceof Error ? e.message : 'The partner could not be loaded.' });
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [creatorId],
  );

  useEffect(() => {
    setDetail(null);
    void load();
  }, [load]);

  const reload = useCallback(() => load(true), [load]);

  // While Mr. Roboto has a change to apply, check every few seconds so the
  // result shows up without a refresh; otherwise every half minute.
  useVisiblePolling(() => {
    if (detail && !loading) void load(true, true);
  }, detail?.discord.pending ? 5_000 : 30_000);

  if (loading && !detail) {
    return (
      <div className="space-y-6" aria-busy="true">
        <div className="space-y-3">
          <SkeletonLoader className="h-3 w-24" />
          <div className="flex items-center gap-3">
            <SkeletonLoader className="w-10 h-10 rounded-md" />
            <SkeletonLoader className="h-7 w-64 max-w-full" />
            <SkeletonLoader className="h-5 w-24 rounded-full" />
          </div>
          <SkeletonLoader className="h-4 w-48" />
        </div>
        <div className="glass-card grid xl:grid-cols-[13rem_minmax(0,1fr)]">
          <div className="p-3 space-y-2 border-b xl:border-b-0 xl:border-r border-glass-200/10">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-9" />
            ))}
          </div>
          <div className="p-4 md:p-5 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-12" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !detail) {
    const notFound = error?.status === 404;
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="PR Management" title="Partner" backTo="/partnerships" backLabel="Partnerships" />
        <EmptyState
          tone={notFound ? 'neutral' : 'danger'}
          icon={AlertCircle}
          title={notFound ? 'This partner does not exist' : 'The partner could not be loaded'}
          description={notFound ? 'The creator may have been deleted. Partner pages belong to a creator in Partnerships.' : error?.message}
          action={
            <>
              {!notFound && (
                <button type="button" onClick={() => void load()} className="glass-button">
                  Retry
                </button>
              )}
              <button type="button" onClick={() => navigate('/partnerships')} className="glass-button-secondary">
                <ArrowLeft className="w-4 h-4" />
                Back to Partnerships
              </button>
            </>
          }
        />
      </div>
    );
  }

  const { creator, discord } = detail;
  const activeMembers = detail.members.filter((m) => m.status === 'active');
  const counts: Partial<Record<SectionId, number>> = {
    team: activeMembers.length,
    updates: detail.open_updates,
    packs: detail.packs.length,
  };
  const accessIssues = activeMembers.filter((m) => m.access_state && !['ok', 'pending'].includes(m.access_state)).length;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="PR Management"
        backTo="/partnerships"
        backLabel="Partnerships"
        icon={creator.type === 'team' ? Users : User}
        title={creator.name}
        titleAdornment={
          <>
            <PartnerPhaseBadge phase={detail.phase} reason={detail.phase_reason} />
            {!discord.managed ? (
              <span
                className="badge badge-neutral"
                title="Mr. Roboto does not manage this partner’s Discord yet. The team is kept here, but nothing changes in Discord until management is turned on."
              >
                Discord not managed
              </span>
            ) : (
              <button type="button" onClick={() => setSection('discord')} className="inline-flex" title="Open the Discord section">
                <PartnerChannelBadge state={discord.observed_state} />
              </button>
            )}
            {discord.managed && discord.pending && (
              <span className="badge badge-warning badge-dot" title="Mr. Roboto has not applied the latest change yet. This page checks every few seconds.">
                Syncing
              </span>
            )}
            {accessIssues > 0 && (
              <button type="button" onClick={() => setSection('team')} className="badge badge-warning" title="Team members whose Discord access needs a look">
                {accessIssues} access {accessIssues === 1 ? 'issue' : 'issues'}
              </button>
            )}
          </>
        }
        description={detail.phase_reason || undefined}
        actions={
          <>
            <PanelTour tourId="partner-page" title="Partner page" steps={partnerPageTour(perms.canEdit)} enabled={perms.canView} {...tourNavigation} />
            <button type="button" onClick={() => void reload()} className="glass-button" title="Refresh" aria-label="Refresh">
              <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button type="button" onClick={() => setShowCreator(true)} className="glass-button-secondary">
              <IdCard className="w-4 h-4" />
              Creator details
            </button>
          </>
        }
      >
        {detail.partnerships.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="eyebrow mr-0.5">Partnerships</span>
            {detail.partnerships.map((p) => (
              <Link
                key={p.id}
                to={`/partnerships/${encodeURIComponent(p.id)}`}
                className="chip inline-flex items-center gap-1.5 hover:border-glass-200/30 hover:text-white transition-colors"
                title="Open the partnership"
              >
                <Handshake className="w-3 h-3 text-glass-400" />
                <span className="truncate max-w-[14rem]">{p.modpack_name ?? 'Creator-level partnership'}</span>
                <PartnershipStatusBadge status={p.status as PartnershipStatus} />
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-xs text-glass-500">This creator has no partnerships yet, so nothing is set up in Discord.</p>
        )}
      </PageHeader>

      <PartnerNoticeBar notice={notice} onDismiss={() => setNotice(null)} />

      <DetailSections sections={detailSections('partner', TABS, counts)} active={section} onChange={setSection} navTour="partner-sections">
        {section === 'team' && (
          <PartnerTeamSection
            creatorId={creator.id}
            creatorName={creator.name}
            members={detail.members}
            managed={discord.managed}
            canEdit={perms.canEdit}
            onChanged={reload}
            onNotice={setNotice}
          />
        )}
        {section === 'discord' && (
          <PartnerDiscordSection detail={detail} canEdit={perms.canEdit} canAdmin={perms.canAdmin} now={now} onChanged={reload} onNotice={setNotice} />
        )}
        {section === 'updates' && <PartnerUpdatesSection detail={detail} canEdit={perms.canEdit} canOpenConfigs={canOpenConfigs} onChanged={reload} onNotice={setNotice} />}
        {section === 'packs' && (
          <PartnerPacksSection creatorId={creator.id} packs={detail.packs} canEdit={perms.canEdit} canOpenConfigs={canOpenConfigs} onChanged={reload} onNotice={setNotice} />
        )}
        {section === 'server-packs' && <PartnerServerPacksSection creatorId={creator.id} now={now} />}
        {section === 'activity' && (
          <PartnerActivityTimeline creatorId={creator.id} refreshKey={refreshKey} names={new Map(detail.members.map((m) => [m.discord_id, memberName(m)]))} />
        )}
      </DetailSections>

      {showCreator && (
        <CreatorDetailModal
          creatorId={creator.id}
          isOpen={showCreator}
          onClose={() => setShowCreator(false)}
          onUpdate={() => void reload()}
          canEdit={perms.canEdit}
          canDelete={perms.canAdmin}
          hidePartnerLink
          onDeleted={() => navigate('/partnerships')}
        />
      )}
    </div>
  );
}
